/**
 * Setting what a grammar question asks for apart from how it asks (CB-23).
 *
 * A question's English often holds two things: the instruction, "Choose the
 * sentence that says", and what the right answer has to mean, "the weather in
 * Beijing is now cold". The meaning is what the reader holds in mind while
 * reading the options, so it is drawn on its own (`modules/lianxi`), and the
 * instruction around it stays plain. The questions are written by hand and
 * their English carries no markup, so the meaning is found by the few ways the
 * questions phrase it, and a question phrased another way is left as it is.
 */

/** Where the meaning starts, after the words introducing it. */
const introductions: readonly RegExp[] = [
  // "The intended meaning is “He does not have any water.”"
  // "Rewrite as a comparison meaning 'My older sister is taller than me.'"
  // An apostrophe inside a word, as in isn't, does not close the quotation.
  /meaning(?: is)? (“[^”]+”|'(?:[^']|'(?=\p{L}))+')/u,
  // "Choose the sentence that correctly says: I don't have a car."
  // "Choose the option that explicitly says he does not eat meat anymore."
  /that (?:correctly |explicitly )?says:? ([^.(]+)/u,
  // "Insert 了 once to say I do not buy newspapers anymore."
  /once to say ([^.]+)/u,
];

/**
 * The span of the English that is the meaning asked for.
 * @param text - The question's English.
 * @returns Where the meaning starts and ends, or nothing.
 */
export function promptTarget(
  text: string,
): { readonly start: number; readonly end: number } | undefined {
  for (const pattern of introductions) {
    const found = pattern.exec(text);
    const meaning = found?.[1]?.trimEnd();

    if (found !== null && meaning !== undefined && meaning !== "") {
      const start = found.index + found[0].indexOf(meaning);

      return { start, end: start + meaning.length };
    }
  }

  return undefined;
}

/**
 * Draw the meaning in every question's English under `root` on its own.
 * @param root - The exercise's block.
 */
export function markPrompts(root: ParentNode): void {
  for (const prompt of root.querySelectorAll<HTMLElement>(
    "[data-cb-yufa-prompt]",
  )) {
    const text = prompt.textContent;
    const target = promptTarget(text);

    if (
      target === undefined ||
      prompt.children.length > 0 ||
      prompt.ownerDocument === null
    ) {
      continue;
    }

    const meaning = prompt.ownerDocument.createElement("strong");

    meaning.className = "lianxi-yufa-target";
    meaning.textContent = text.slice(target.start, target.end);
    prompt.replaceChildren(
      text.slice(0, target.start),
      meaning,
      text.slice(target.end),
    );
  }
}
